/*
 * A plain textarea with agent chat's `@` behavior: typing `@` opens the
 * MentionPicker over `candidates`, a pick becomes a pill (the display shows
 * `@Name`; `toWire(value)` gives the `@Name <user:uid>` tokens to send), and
 * Backspace into a pill removes it whole. The text is transparent and a mirror
 * behind it draws the pills, so the glyphs line up.
 */
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent
} from "react";

import { cn } from "@/lib/utils";

import {
  activeMentionAt,
  filterMentions,
  insertMentionAt,
  modelSegments,
  reconcileDisplay,
  targetOf,
  type ActiveMention,
  type ComposerModel,
  type MentionCandidate
} from "./mentions";
import { MentionPicker, type MentionPickerProps } from "./MentionPicker";

export type MentionTextareaHandle = {
  focus: () => void;
  /** Type an `@` at the end and open the picker (the composer's @ button). */
  startMention: () => void;
};

export type MentionTextareaProps = {
  value: ComposerModel;
  onChange: (next: ComposerModel) => void;
  candidates: MentionCandidate[];
  /** Enter without Shift while the picker is closed. */
  onSubmit?: () => void;
  placeholder?: string;
  disabled?: boolean;
  groupLabel?: MentionPickerProps["groupLabel"];
  "aria-label"?: string;
  className?: string;
};

/** Shared by the textarea and its mirror so their glyphs line up. */
const METRICS =
  "block w-full whitespace-pre-wrap break-words p-0 text-base leading-5";

export const MentionTextarea = forwardRef<
  MentionTextareaHandle,
  MentionTextareaProps
>(function MentionTextarea(
  {
    value,
    onChange,
    candidates,
    onSubmit,
    placeholder,
    disabled,
    groupLabel,
    "aria-label": ariaLabel,
    className
  },
  ref
) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  const modelRef = useRef(value);
  modelRef.current = value;
  const [mention, setMention] = useState<ActiveMention | null>(null);
  const [highlight, setHighlight] = useState(0);
  const pendingRef = useRef<{ caret?: number; reopen?: boolean } | null>(null);

  const matches = useMemo(
    () => (mention ? filterMentions(candidates, mention.query) : []),
    [candidates, mention]
  );
  const open = Boolean(mention && matches.length);

  const syncMention = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const next = activeMentionAt(
      el.value,
      el.selectionStart ?? el.value.length,
      modelRef.current.mentions
    );
    setMention((prev) =>
      prev?.start === next?.start && prev?.query === next?.query ? prev : next
    );
    setHighlight(0);
  }, []);

  const commit = useCallback(
    (next: { model: ComposerModel; caret?: number }, reopen = false) => {
      modelRef.current = next.model;
      onChange(next.model);
      pendingRef.current = { caret: next.caret, reopen };
    },
    [onChange]
  );

  useLayoutEffect(() => {
    const pending = pendingRef.current;
    const el = textareaRef.current;
    if (!pending || !el) return;
    pendingRef.current = null;
    el.focus();
    if (pending.caret != null)
      el.setSelectionRange(pending.caret, pending.caret);
    if (pending.reopen) syncMention();
  });

  useImperativeHandle(
    ref,
    () => ({
      focus: () => textareaRef.current?.focus(),
      startMention: () => {
        const cur = modelRef.current.display;
        const sep = cur && !/\s$/.test(cur) ? " " : "";
        commit(
          {
            model: reconcileDisplay(modelRef.current, `${cur}${sep}@`).model,
            caret: cur.length + sep.length + 1
          },
          true
        );
      }
    }),
    [commit]
  );

  const onTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.currentTarget.value;
    const out = reconcileDisplay(modelRef.current, next);
    if (out.model.display !== next) {
      setMention(null);
      commit(out);
      return;
    }
    modelRef.current = out.model;
    onChange(out.model);
    syncMention();
  };

  const choose = (c: MentionCandidate) => {
    const el = textareaRef.current;
    if (!el || !mention) return;
    setMention(null);
    commit(
      insertMentionAt(
        modelRef.current,
        mention,
        el.selectionStart ?? el.value.length,
        targetOf(c)
      )
    );
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (open) {
      if (e.key === "ArrowDown") setHighlight((h) => (h + 1) % matches.length);
      else if (e.key === "ArrowUp")
        setHighlight((h) => (h - 1 + matches.length) % matches.length);
      else if (e.key === "Enter" || e.key === "Tab")
        choose(matches[highlight] ?? matches[0]);
      else if (e.key === "Escape") setMention(null);
      else return;
      e.preventDefault();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onSubmit?.();
    }
  };

  return (
    <div className={cn("relative", className)}>
      {open ? (
        <MentionPicker
          size="md"
          matches={matches}
          highlight={highlight}
          onHighlight={setHighlight}
          onPick={choose}
          groupLabel={groupLabel}
          className="absolute bottom-full left-0 z-20 mb-2 w-72"
        />
      ) : null}
      <div
        ref={mirrorRef}
        aria-hidden
        data-testid="mention-textarea-mirror"
        className={cn(
          METRICS,
          "pointer-events-none absolute inset-0 overflow-hidden text-fg"
        )}
      >
        {modelSegments(value).map((seg, i) =>
          seg.type === "text" ? (
            <span key={i}>{seg.text}</span>
          ) : (
            <span
              key={i}
              className="-mx-0.5 rounded-xs bg-accent-soft px-0.5 text-accent-seed"
            >
              {seg.text}
            </span>
          )
        )}
        {"\u200b"}
      </div>
      <textarea
        ref={textareaRef}
        rows={1}
        value={value.display}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={onTextChange}
        onKeyDown={onKeyDown}
        onKeyUp={syncMention}
        onClick={syncMention}
        onBlur={() => setTimeout(() => setMention(null), 120)}
        onScroll={(e) => {
          if (mirrorRef.current)
            mirrorRef.current.scrollTop = e.currentTarget.scrollTop;
        }}
        className={cn(
          METRICS,
          "relative z-10 max-h-40 min-h-5 resize-none overflow-y-auto border-0 bg-transparent text-transparent caret-fg outline-none placeholder:text-fg-4 field-sizing-content disabled:opacity-50"
        )}
      />
    </div>
  );
});
